import { Col, FormGroup, Label } from 'reactstrap';
import React, { FunctionComponent, useState } from 'react';
import { compose } from 'recompose';
import { ApiDataRequest, withApiData, ApiDataBinding } from 'react-api-data';
import { withTranslation, WithTranslation } from 'react-i18next';

// constants
import { ApiResponse, Asn, LinkFull, ReactApiDataActions, Vlan } from 'constants/api';

// components
import FormModal from './FormModal';
import { Roles } from 'constants/literals';
import AsnSelectContract from 'components/utils/AsnSelectContract';
import EditBtnConditionalRenderer from 'components/utils/EditBtnConditionalRenderer';
import Tooltip from 'components/utils/Tooltip';

interface InputProps {
    link: LinkFull;
    linkVlan: Vlan;
    defaultAsn: number;
    endpointKey: string;
}

interface EnhanceProps {
    request: ApiDataRequest;
    asnChangeRequest: ApiDataBinding<ApiResponse<Asn>>;
    apiDataActions: ReactApiDataActions;
    linkRequest: ApiDataBinding<LinkFull>;
    userRoles: Roles[];
}

type Props = EnhanceProps & InputProps & WithTranslation;

const enhance = compose<Props, InputProps>(
    withTranslation(),
    withApiData({
        asnChangeRequest: 'putVlanAsn',
        linkRequest: 'getMemberLink',
    })
);

type FormValues = { as_number: number | null; organisation_id: number | null };

const AsnModal: FunctionComponent<Props> = ({ asnChangeRequest, linkRequest, defaultAsn, link, linkVlan, t }) => {
    const [isOpen, setIsOpen] = useState(false);

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit(values: FormValues) {
        return asnChangeRequest.perform({ vlanId: linkVlan.uuid, exchange: link.exchange.short_name, linkId: link.uuid }, values);
    }

    function onSucces() {
        linkRequest.invalidateCache();
        linkRequest.perform({ linkId: link.uuid, exchange: link.exchange.short_name });
    }

    return (
        <React.Fragment>
            <FormModal
                header={t('addAsnModal.headerTitle')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={{ as_number: defaultAsn, organisation_id: null }}
                onSubmit={onSubmit}
                onSuccess={onSucces}
                successMessage={t('notification.changeAsnSuccessful')}
            >
                {({ values, setValues }) => (
                    <React.Fragment>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="as_number">{t('addVlanModal.asnSelect')}</Label>
                            </Col>
                            <Col md={9}>
                                <AsnSelectContract
                                    value={defaultAsn}
                                    onChange={(asnNumber) => {
                                        setValues({ ...values, as_number: asnNumber });
                                    }}
                                    contractUuid={linkVlan.contract_uuid}
                                    exchange={linkVlan.exchange}
                                />
                            </Col>
                        </FormGroup>
                    </React.Fragment>
                )}
            </FormModal>

            <EditBtnConditionalRenderer
                data-test="edit-btn-renderer"
                resourceStatus={link.status}
                roles={[Roles.admin, Roles.customer, Roles.ixaas_client]}
            >
                <Tooltip
                    data-test="edit-btn-tooltip"
                    triggerBtn={
                        <i className="fas fa-edit fa-pull-right mt-1 cursor-pointer text-primary" onClick={toggle} />
                    }
                >
                    {t('changeAsnModal.headerTitle')}
                </Tooltip>
            </EditBtnConditionalRenderer>
        </React.Fragment>
    );
};

export default enhance(AsnModal);
